import { useEffect, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  MarketButton,
  MarketDropdown,
  MarketHeader,
  MarketList,
  MarketPopover,
  MarketRow,
} from '@market/react'
import { Discount, view as squareViewApi } from '@squareup/sq-addons-web-sdk'
import qs from 'qs'
import { ReactComponent as ArrowLeft } from '@market/icons/assets/svg/arrow-left.svg'
import { ReactComponent as EllipsisHorizontal } from '@market/icons/assets/svg/ellipsis-horizontal.svg'

import { RequestContext } from 'utils/pos'
import LoadingScreen from 'components/Loading'
import ConnectionError from 'components/ConnectionError'
import { getCustomerDisplayName } from 'utils/customer'
import { useAppDispatch } from 'store'
import {
  useAddCustomerToSale,
  useCustomer,
  useCustomerContext,
  useIsCustomerAddedToSale,
  useRemoveCustomerFromSale,
} from 'hooks/customer'
import { Offer } from 'api/types'
import { ApplyOfferResult, RemoveOfferResult, useLoyalty } from 'hooks/loyalty'
import { clearCustomer } from 'reducers/customer'
import AvailableOfferCard from './AvailableOfferCard'
import Switch from 'components/Switch'
import { buildTranslate } from 'locales'
import { useCustomerLoyaltyInfo } from './helpers'
import { err } from 'utils/result'
import OfferCard from 'components/OfferCard'
import RedeemedOfferCard from './RedeemedOfferCard'
import {
  AppliedDiscount,
  formatPointsBalance,
  getOrderDiscount,
} from 'utils/loyalty'

const t = buildTranslate('customer')

type RequestType =
  | 'addCustomerToSale'
  | 'removeCustomerFromSale'
  | 'addOffer'
  | 'removeOffer'
type RequestState = 'init' | 'pending' | 'success' | 'error'

const CustomerScreen = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const customer = useCustomer()
  const customerId = customer?.referenceId ?? null

  const {
    pointBalance,
    availableOffers,
    unavailableOffers,
    pointsCurrencySingular,
    pointsCurrencyPlural,
    isFetchingOffers,
    isErrorFetchingOffers,
    fetchOffers,
  } = useCustomerLoyaltyInfo(customer)

  const isCustomerAddedToSale = useIsCustomerAddedToSale()
  const customerContext = useCustomerContext()
  const { applyOffer, removeOffer } = useLoyalty()
  const { addCustomerToSale } = useAddCustomerToSale()
  const { removeCustomerFromSale: handleRemoveCustomerFromSale } =
    useRemoveCustomerFromSale()
  const [requestsState, _setRequestsState] = useState<
    Record<RequestType, RequestState>
  >({
    addCustomerToSale: 'init',
    removeCustomerFromSale: 'init',
    addOffer: 'init',
    removeOffer: 'init',
  })
  const [appliedDiscount, setAppliedDiscount] = useState<Discount | null>(null)

  useEffect(() => {
    async function init() {
      const discount = await getOrderDiscount()
      if (discount) {
        setAppliedDiscount(discount as unknown as Discount)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateRequestsState = useCallback(
    (updates: Partial<typeof requestsState>) => {
      _setRequestsState(state => ({
        ...state,
        ...updates,
      }))
    },
    []
  )

  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  })
  const isCheckoutFlow = queryParams['checking_out'] === 'true'
  const currencyName =
    pointBalance === 1 ? pointsCurrencySingular : pointsCurrencyPlural

  const tryRemoveCustomerFromSale = async () => {
    updateRequestsState({ removeCustomerFromSale: 'pending' })

    const result = await handleRemoveCustomerFromSale()
    if (result.isOk()) {
      setAppliedDiscount(null)
      fetchOffers()
    }
    updateRequestsState({
      removeCustomerFromSale: result.isErr() ? 'error' : 'success',
    })
    return result
  }

  const tryAddCustomerToSale = useCallback(async () => {
    updateRequestsState({ addCustomerToSale: 'pending' })
    const result = await addCustomerToSale(customer)
    updateRequestsState({
      addCustomerToSale: result.isErr() ? 'error' : 'success',
    })

    return result
  }, [customer, addCustomerToSale, updateRequestsState])

  const onApplyOffer = useCallback(
    async (offer: Offer): Promise<ApplyOfferResult> => {
      if (!customerId) {
        return err(new Error('Customer is not defined'))
      }

      if (!isCustomerAddedToSale) {
        const addResult = await tryAddCustomerToSale()
        if (addResult.isErr()) {
          return err(addResult.error)
        }
      }

      updateRequestsState({ addOffer: 'pending' })
      const applyResult = await applyOffer(offer, customerId)

      if (applyResult.isOk()) {
        await fetchOffers()
        setAppliedDiscount(applyResult.value)
      }

      updateRequestsState({
        addOffer: applyResult.isErr() ? 'error' : 'success',
      })
      return applyResult
    },
    [
      customerId,
      isCustomerAddedToSale,
      updateRequestsState,
      applyOffer,
      tryAddCustomerToSale,
      fetchOffers,
    ]
  )

  const onRemoveOffer = useCallback(
    async (offer: AppliedDiscount): Promise<RemoveOfferResult> => {
      if (!customerId) {
        return err(new Error('Customer is not defined'))
      }

      if (!isCustomerAddedToSale) {
        return err(new Error('Customer is not added to sale'))
      }

      updateRequestsState({ removeOffer: 'pending' })
      const removeResult = await removeOffer(offer, customerId)
      if (removeResult.isOk()) {
        await fetchOffers()
        setAppliedDiscount(null)
      }

      updateRequestsState({
        removeOffer: removeResult.isErr() ? 'error' : 'success',
      })
      return removeResult
    },
    [
      customerId,
      fetchOffers,
      isCustomerAddedToSale,
      removeOffer,
      updateRequestsState,
    ]
  )

  const onClickBack = () => {
    if (isCustomerAddedToSale) {
      return
    } else {
      dispatch(clearCustomer())

      // If we fetch customer through barcode scanner scan, we always go back
      // to the main page. If we fetch customer through the SDK, it means that
      // the customer was already added to the order, and we automatically
      // routed to the customer page. Hence, go back to the main screen.
      if (
        customerContext === RequestContext.BARCODE_SCANNER ||
        customerContext === RequestContext.SDK
      ) {
        history.replace('/' + history.location.search)
      } else {
        history.goBack()
      }
    }
  }

  const onClickContinueButton = useCallback(() => {
    squareViewApi.continueClicked()
  }, [])

  const onClickTryAgain = () => {
    if (requestsState.addCustomerToSale === 'error' && customer) {
      tryAddCustomerToSale()
    }

    if (requestsState.removeCustomerFromSale === 'error') {
      tryRemoveCustomerFromSale()
    }

    if (isErrorFetchingOffers) {
      fetchOffers()
    }
  }

  useEffect(() => {
    if (!isCheckoutFlow) {
      return
    }

    const eventListener = (e: any) => {
      if (e.key === 'Enter') {
        onClickContinueButton()
      }
    }

    window.addEventListener('keydown', eventListener)

    return () => {
      window.removeEventListener('keydown', eventListener)
    }
  }, [isCheckoutFlow, onClickContinueButton])

  const isPending = Object.values(requestsState).some(r => r === 'pending')

  const isError =
    isErrorFetchingOffers ||
    requestsState.addCustomerToSale === 'error' ||
    requestsState.removeCustomerFromSale === 'error'

  if (isError || !customer) {
    return <ConnectionError onClickTryAgain={onClickTryAgain} />
  }

  const backButtonDisabledProp = (() => {
    if (isCustomerAddedToSale) {
      return {
        disabled: true,
      }
    }

    return {}
  })()

  const continueButtonDisabledProp = (() => {
    if (isPending) {
      return {
        disabled: true,
      }
    }

    return {}
  })()

  return (
    <div className="market-grid-container">
      <MarketHeader className="market-grid-item-full addons-header" compact>
        <MarketButton
          data-testid="customer-back-btn"
          onClick={onClickBack}
          rank="secondary"
          slot="navigation"
          {...backButtonDisabledProp}
        >
          <div slot="icon" className="addons-icon">
            <ArrowLeft className="addons-back-icon" width="24" height="24" />
          </div>
        </MarketButton>

        <div slot="actions" style={{ display: 'flex' }}>
          <Switch
            condition={isCustomerAddedToSale}
            fallback={
              <MarketButton
                data-testid="add-customer-to-sale-btn"
                onClick={tryAddCustomerToSale}
                isLoading={
                  requestsState.addCustomerToSale === 'pending' || undefined
                }
                rank="primary"
              >
                {t('addToSale')}
              </MarketButton>
            }
          >
            <>
              <MarketDropdown
                slot="actions"
                popover-placement="bottom-end"
                style={{ marginRight: '15px' }}
              >
                <MarketButton
                  slot="trigger"
                  rank="secondary"
                  data-testid="ellipsis-btn"
                >
                  <div slot="icon" className="addons-icon">
                    <EllipsisHorizontal className="addons-ellipsis-icon" />
                  </div>
                </MarketButton>
                <MarketPopover slot="popover">
                  <MarketList transient>
                    <MarketRow
                      onClick={tryRemoveCustomerFromSale}
                      data-testid="remove-from-sale-popover-row"
                    >
                      {t('removeFromSale')}
                    </MarketRow>
                  </MarketList>
                </MarketPopover>
              </MarketDropdown>
              <MarketButton
                data-testid={`customer-${
                  isCheckoutFlow ? 'continue' : 'done'
                }-btn`}
                onClick={onClickContinueButton}
                rank="primary"
                slot="actions"
                {...continueButtonDisabledProp}
              >
                {isCheckoutFlow ? t('continue') : t('done')}
              </MarketButton>
            </>
          </Switch>
        </div>
      </MarketHeader>

      <MarketList
        className="market-grid-item-full"
        data-testid="customer-information"
      >
        <div>
          <h2>{getCustomerDisplayName(customer)}</h2>
        </div>

        <div style={{ marginBottom: '48px' }}>
          <p>
            {formatPointsBalance(pointBalance ?? 0)} {currencyName}
          </p>
        </div>

        {!!appliedDiscount && (
          <div>
            <div>
              <h3>{t('appliedReward')}</h3>
              <p>{t('onlyOneAllowed')}</p>
            </div>
            <RedeemedOfferCard
              discount={appliedDiscount}
              onRemoveOffer={onRemoveOffer}
            />
          </div>
        )}

        <Switch
          condition={!isFetchingOffers}
          fallback={<LoadingScreen transparent />}
        >
          <div>
            <h3>{t('availableRewards')}</h3>
          </div>
          <Switch
            condition={availableOffers.length > 0}
            fallback={
              <div style={{ marginTop: '32px' }}>
                <p>{t('noRewards')}</p>
              </div>
            }
          >
            <div data-testid="available-offers">
              {availableOffers.map(offer => (
                <AvailableOfferCard
                  key={offer.id}
                  offer={offer}
                  onApplyOffer={onApplyOffer}
                  isDisabled={!!appliedDiscount || isPending}
                  pointsCurrencySingular={pointsCurrencySingular}
                  pointsCurrencyPlural={pointsCurrencyPlural}
                />
              ))}
            </div>
          </Switch>

          <div style={{ marginTop: '32px' }}>
            <h3>{t('unavailableRewards')}</h3>
          </div>
          <Switch
            condition={unavailableOffers.length > 0}
            fallback={
              <div style={{ marginTop: '32px' }}>
                <p>{t('noRewards')}</p>
              </div>
            }
          >
            <div data-testid="unavailable-offers">
              {unavailableOffers.map(({ id, name }) => {
                return (
                  <OfferCard kind="unavailable-offer" offerId={id} key={id}>
                    <OfferCard.Label>{name}</OfferCard.Label>
                    <OfferCard.Button isDisabled>
                      {t('redeem')}
                    </OfferCard.Button>
                  </OfferCard>
                )
              })}
            </div>
          </Switch>
        </Switch>
      </MarketList>
    </div>
  )
}

export default CustomerScreen
